import type { TransportVehicleDrilldownDto } from "@bi/types";
import type { AttentionPoint, IntelSeverity } from "./intelligence/executive";
import type { HeroStatus } from "../components/intelligence/SystemHero";
import { fmtBRL, fmtNumber, tidyText } from "./format";

/**
 * Regras do Raio-X da placa no padrão da Visão Executiva: a fila "O que pede
 * ação" (alertas da API + conferências de KM e pedágio) e o selo da manchete.
 * Puro, sem React, para ser testado.
 */

type Drill = TransportVehicleDrilldownDto;

const fmt2 = (v: number) => v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const SEV_ORDER: Record<IntelSeverity, number> = { critical: 0, warning: 1, info: 2 };

/** Mesmo corte de margem do backend e da Margem por Placa (12% bom, 6% atenção). */
export function marginTone(pct: number | null): "warning" | "critical" | null {
  return pct == null ? null : pct >= 0.12 ? null : pct >= 0.06 ? "warning" : "critical";
}

/** Custo/km contra a média da frota: mesmos cortes do alerta da API (+20% atenção, +50% crítico). */
export function costPerKmTone(cpk: number | null, fleet: number | null): "warning" | "critical" | null {
  if (cpk == null || fleet == null || fleet <= 0) return null;
  const ratio = cpk / fleet;
  return ratio >= 1.5 ? "critical" : ratio >= 1.2 ? "warning" : null;
}

/** Consumo contra a média da frota: só com base confiável, mesmo corte do alerta da API (15% pior). */
export function consumptionTone(kml: number | null, fleet: number | null, reliable: boolean): "warning" | null {
  if (!reliable || kml == null || fleet == null || fleet <= 0) return null;
  return kml / fleet - 1 <= -0.15 ? "warning" : null;
}

/** "+12% vs. frota (R$ 4,20)" / "8% abaixo da frota". */
export function vsFleet(cur: number | null, fleet: number | null, unit: (v: number) => string): string | null {
  if (cur == null || fleet == null || fleet <= 0) return null;
  const dev = Math.round((cur / fleet - 1) * 100);
  if (dev === 0) return `Igual à média da frota (${unit(fleet)})`;
  return `${Math.abs(dev)}% ${dev > 0 ? "acima" : "abaixo"} da média da frota (${unit(fleet)})`;
}

/** O que fazer, por tipo de alerta, quando a API não manda recomendação. */
const FALLBACK_ACTION: Record<string, string> = {
  margin_negative: "Abra o custo direto abaixo e veja qual parcela passou da receita.",
  cost_per_km_high: "Compare combustível e manutenção desta placa com o período anterior, no quadro de custos abaixo.",
  poor_consumption: "Confira os abastecimentos do período e a rota. É indício a investigar, não causa confirmada.",
  idle_with_cost: "Confirme se a placa estava parada ou se os CT-es estão em outra placa do conjunto.",
  maintenance_rising: "Veja as manutenções do período na lista abaixo.",
  tire_low_mm: "Programe a troca dos pneus na aba Pneus.",
  tire_near_change: "Programe a troca dos pneus na aba Pneus.",
};

/** Qual é o impacto? Só diferença medida com os números da tela, nunca causa presumida. */
function alertImpact(kind: string, d: Drill): string {
  const r = d.result;
  const km = d.production.km;
  switch (kind) {
    case "margin_negative":
      return `${fmtBRL(r.margin)} de margem`;
    case "cost_per_km_high":
      if (r.costPerKm != null && r.fleet.costPerKm != null && km > 0) {
        return `${fmtBRL((r.costPerKm - r.fleet.costPerKm) * km)} acima da média no período`;
      }
      return r.costPerKm != null ? `R$ ${fmt2(r.costPerKm)} por km` : "Custo por km";
    case "poor_consumption": {
      const kml = d.operation.kmPerLiter;
      const fleet = r.fleet.kmPerLiter;
      if (kml != null && fleet != null && kml > 0 && fleet > 0 && km > 0) {
        const extra = km / kml - km / fleet;
        if (extra > 0) return `${fmtNumber(Math.round(extra))} litros a mais que a média`;
      }
      return kml != null ? `${fmt2(kml)} km/l` : "Consumo";
    }
    case "idle_with_cost":
      return `${fmtBRL(d.costs.direct.total)} sem receita`;
    case "maintenance_rising":
      return `${fmtBRL(d.costs.direct.maintenance)} em manutenção`;
    case "tire_low_mm":
    case "tire_near_change":
      return "Pneus da placa";
    default:
      return "Ver os números";
  }
}

/**
 * A fila "O que pede ação" do Raio-X: os alertas da API e as conferências de KM
 * e pedágio que deram diferença, do mais urgente para o menos urgente.
 */
export function vehicleAttention(d: Drill): AttentionPoint[] {
  const pts: Omit<AttentionPoint, "priority">[] = [];

  d.alerts.forEach((a, i) => {
    const tires = /^(\d+)\s+pneu/.exec(a.title)?.[1];
    pts.push({
      id: `${a.kind}-${i}`,
      severity: a.severity,
      memory: null,
      title: tidyText(a.title),
      fact: tidyText(a.evidence),
      impact: tires ? `${tires} pneu${tires === "1" ? "" : "s"}` : alertImpact(a.kind, d),
      action: tidyText(a.recommendation ?? FALLBACK_ACTION[a.kind] ?? "Abra os números abaixo e confira a origem."),
      evidence: [{ label: "Evidência", value: tidyText(a.evidence) }],
    });
  });

  const km = d.kmAudit;
  if (km && km.status === "divergencia_alta" && km.diffKm != null) {
    pts.push({
      id: "km-divergente",
      severity: "warning",
      memory: null,
      title: "KM do abastecimento diferente da telemetria",
      fact: `O hodômetro digitado nos abastecimentos soma ${km.fuelKm != null ? fmtNumber(Math.round(km.fuelKm)) : "?"} km e o rastreador leu ${km.telemetryKm != null ? fmtNumber(Math.round(km.telemetryKm)) : "?"} km no período.`,
      impact: `${km.diffKm > 0 ? "+" : ""}${fmtNumber(Math.round(km.diffKm))} km${km.diffPct != null ? ` (${(km.diffPct * 100).toFixed(1).replace(".", ",")}%)` : ""}`,
      action: "Confira os hodômetros digitados nos abastecimentos do período, na lista abaixo.",
      evidence: [
        { label: "KM do abastecimento", value: km.fuelKm != null ? fmtNumber(Math.round(km.fuelKm)) : "sem dado" },
        { label: `KM da telemetria (${km.source})`, value: km.telemetryKm != null ? fmtNumber(Math.round(km.telemetryKm)) : "sem dado" },
        ...(km.coverage ? [{ label: "Cobertura da telemetria", value: `${Math.round(km.coverage.coversPct * 100)}% do período` }] : []),
      ],
    });
  }

  const toll = d.tollAudit;
  if (toll && !toll.periodMismatch && toll.diffValue != null && toll.diffPct != null && Math.abs(toll.diffPct) > 0.1) {
    const paidMore = toll.diffValue > 0;
    pts.push({
      id: "pedagio-divergente",
      severity: paidMore ? "warning" : "info",
      memory: null,
      title: paidMore ? "Pedágio pago acima do cobrado no frete" : "Pedágio cobrado no frete acima do pago",
      fact: `Foram pagos ${fmtBRL(toll.paidTolls)} na praça (${toll.source}) e cobrados ${fmtBRL(toll.freightTolls ?? 0)} no frete.`,
      impact: `${paidMore ? "+" : ""}${fmtBRL(toll.diffValue)}`,
      action: "Abra Pedágio no custo direto para ver as passagens por rota e CT-e.",
      evidence: [
        { label: "Pago na praça", value: fmtBRL(toll.paidTolls), note: `${toll.tollCount} passagens` },
        { label: "Cobrado no frete", value: fmtBRL(toll.freightTolls ?? 0) },
        { label: "Fatura", value: toll.invoiceNumbers.join(", ") || "sem número" },
      ],
    });
  }

  return pts
    .map((p, i) => ({ p, i }))
    .sort((a, b) => SEV_ORDER[a.p.severity] - SEV_ORDER[b.p.severity] || a.i - b.i)
    .map(({ p }, i) => ({ ...p, priority: i + 1 }));
}

/** Selo da manchete: o pior entre o status da placa e a fila de pontos de atenção. */
export function vehicleHeroStatus(d: Drill, points: AttentionPoint[]): HeroStatus {
  if (d.result.status === "critico" || points.some((p) => p.severity === "critical")) return "critical";
  if (d.result.status === "atencao" || points.some((p) => p.severity === "warning")) return "warning";
  return "ok";
}
